import { createSlice } from '@reduxjs/toolkit';
import {
  playPupCard, stagePupCard, unstagePupCard, queuePupCard, unqueuePupCard, sniperGuess,
  pickPupTarget, pickPupDiscard, pickPupHandDiscard, pickPupCardTarget, multiDiscardPupCards,
  buryPupCard, performPupTrick, pickPupOptIn, pickPupValue, votePupPlay,
  pickContractSeat,
} from './pupApi';

const roomMaps = () => ({
  inventory: {},
  staged: {},
  // Positionally aligned with `staged`: which staged copies form a COMPLETE set and are therefore
  // already sold to the next Exchange stage. Server-derived (the same flags unstageCard enforces),
  // never computed here — the box hides those cards so nobody tugs at one it can't give back.
  stagedCommitted: {},
  // The AUTO-PLAY box: cards the server will play for this player at the next moment each is
  // legally playable. Like `staged` it is private, lives in its own card zone server-side (so the
  // dock excludes it for free), and is NOT swept on a deal — it persists across rounds.
  queued: {},
  // Good economy's discounted exchange rate, private to this player. Only read by the
  // conditional-card ring (it's the one precondition the public payload can't show).
  exchangeRateTwo: {},
  locked: {},
  cardInstances: {},
  plays: {},
  publicReveals: {},
  peeks: {},
  myPeeked: {},
  // Which cards are WILD, as POSITIONS: [{ zone: 'COMMUNITY'|'HOLE', seat, index }]. Wildness is
  // a property of a CARD, never of a card's value, so it can only be addressed positionally — a
  // token would match every card that happens to share it (two cards legitimately can, because
  // Suit Change / Super Suit / Set Ace can rewrite one into another's value).
  // AUTHORITATIVE and REPLACE-semantics: it comes from the game payload/snapshot, so it survives
  // a reconnect. The PUP_CARDS_REVEALED 'wild' announce is only a flash and must not append here,
  // or a client that saw both would double-count.
  wildRefs: {},
  // This player's own hole-card IDENTITIES, positionally aligned with their hole cards. Private,
  // like the cards. Used to look a card's provenance up by identity rather than by slot.
  myHoleCardIds: {},
  // Card provenance, keyed by CARD ID (not by position): { [roomId]: { [cardId]: [mods] } }.
  cardMods: {},
  notices: {},
  deckPeeks: {},
  sniperGuessUsed: {},
});

const initialState = { ...roomMaps(), error: null };

const clearRoom = (state, roomId) => {
  Object.keys(roomMaps()).forEach((key) => delete state[key][roomId]);
};

const applyPrivateState = (state, payload = {}) => {
  const roomId = payload.roomId;
  if (roomId == null) return;
  if (payload.pupCards !== undefined) state.inventory[roomId] = payload.pupCards || [];
  if (payload.pupCardInstances !== undefined) {
    state.cardInstances[roomId] = payload.pupCardInstances || [];
  }
  if (payload.stagedPupCards !== undefined) state.staged[roomId] = payload.stagedPupCards || [];
  if (payload.stagedCommitted !== undefined) {
    state.stagedCommitted[roomId] = payload.stagedCommitted || [];
  }
  if (payload.queuedPupCards !== undefined) state.queued[roomId] = payload.queuedPupCards || [];
  if (payload.exchangeRateTwo !== undefined) {
    state.exchangeRateTwo[roomId] = !!payload.exchangeRateTwo;
  }
  if (payload.lockedPupCards !== undefined) state.locked[roomId] = payload.lockedPupCards || [];
  if (payload.holeCardIds !== undefined) {
    state.myHoleCardIds[roomId] = parseCardIds(payload.holeCardIds);
  }
};

// A positional card-id CSV ("7 8 9") as an array of strings, aligned with the card tokens at the
// same positions. Strings, not numbers, so they can key a plain object without surprises.
const parseCardIds = (csv) => (
  typeof csv === 'string' && csv.trim() ? csv.trim().split(/\s+/) : []
);

const pupSlice = createSlice({
  name: 'pup',
  initialState,
  reducers: {
    privateStateReceived(state, action) { applyPrivateState(state, action.payload); },
    snapshotReceived(state, action) {
      const { roomId, privateState, pendingPupPlays } = action.payload || {};
      applyPrivateState(state, { roomId, ...(privateState || {}) });
      if (pendingPupPlays?.length) {
        state.plays[roomId] = pendingPupPlays.map((play) => ({ ...play, receivedAt: Date.now() }));
      } else {
        delete state.plays[roomId];
      }
    },
    roomCleared(state, action) { clearRoom(state, action.payload.roomId ?? action.payload); },
    allRoomsCleared(state) { Object.assign(state, roomMaps()); },
    pupPlayPending(state, action) {
      const {
        roomId, playId, cardInstanceId, mode, seat, card, scope, optedSeats,
        durationMs, startTime, deadline, yesSeats, noSeats, currentPickerSeat,
        picks, counteredPlayId, counteredCard, buriesLeft,
      } = action.payload;
      const entry = {
        playId: playId ?? null, cardInstanceId: cardInstanceId ?? null,
        mode: mode || 'targeting', seat, targetSeat: null, card,
        scope: scope || null, zone: null, cardIndex: null,
        buriesLeft: buriesLeft ?? null, optedSeats: optedSeats || null,
        yesSeats: yesSeats || null, noSeats: noSeats || null,
        currentPickerSeat: currentPickerSeat ?? null, picks: picks || null,
        counteredPlayId: counteredPlayId ?? null, counteredCard: counteredCard || null,
        durationMs, startTime, deadline, receivedAt: Date.now(),
      };
      if (!state.plays[roomId]) state.plays[roomId] = [];
      const index = playId != null
        ? state.plays[roomId].findIndex((play) => play.playId === playId) : -1;
      if (index >= 0) state.plays[roomId][index] = entry;
      else state.plays[roomId].push(entry);
    },
    pupTargetChosen(state, action) {
      const { roomId, playId, seat, targetSeat, card, zone, cardIndex, durationMs, startTime } = action.payload;
      const stack = state.plays[roomId];
      if (!stack?.length) return;
      const index = playId != null ? stack.findIndex((play) => play.playId === playId) : stack.length - 1;
      if (index < 0) return;
      stack[index] = {
        ...stack[index], mode: zone ? 'cardLine' : 'line', seat, targetSeat, card,
        zone: zone || null, cardIndex: cardIndex ?? null,
        durationMs, startTime, receivedAt: Date.now(),
      };
    },
    pupPlayCleared(state, action) {
      const { roomId, playId } = action.payload;
      if (playId == null) { delete state.plays[roomId]; return; }
      const next = (state.plays[roomId] || []).filter((play) => play.playId !== playId);
      if (next.length) state.plays[roomId] = next;
      else delete state.plays[roomId];
    },
    // The authoritative wild list, straight off the game payload / snapshot. REPLACE, never
    // merge: the server sends the complete set every time, so a stale entry can't survive and a
    // reconnect restores the badges exactly.
    wildCardsReceived(state, action) {
      const { roomId, wildCards } = action.payload || {};
      if (roomId == null) return;
      state.wildRefs[roomId] = Array.isArray(wildCards) ? wildCards : [];
    },
    pupCardsRevealed(state, action) {
      const { roomId, kind, reveals } = action.payload;
      if (!Array.isArray(reveals)) return;
      if (kind === 'wild') {
        // Presentation only. This announce is the "a card went wild" flash; it names a POSITION
        // and carries no token (a wild hole card's value must never go room-wide). The wild STATE
        // comes from the game payload, which is authoritative and survives a reconnect — appending
        // here as well would double-count.
        return;
      }
      const bySeat = { ...(state.publicReveals[roomId] || {}) };
      reveals.forEach((reveal) => {
        if (!reveal || reveal.seat == null || reveal.seat < 0 || !reveal.cards?.length) return;
        const previous = bySeat[reveal.seat] || [];
        bySeat[reveal.seat] = [...previous, ...reveal.cards.filter((card) => !previous.includes(card))];
      });
      state.publicReveals[roomId] = bySeat;
    },
    pupPrivateReveal(state, action) {
      const { roomId, kind, cards, seat, deckSize } = action.payload;
      const sizeKnown = typeof deckSize === 'number' && deckSize >= 0;
      if (kind === 'deck' && cards?.length) {
        // deckSize = the deck's card count at the peek. GameTable drops peeked cards from the
        // front as the live game.deckRemaining falls below it, and hides the strip when none
        // remain (a draw/burn consumed them). Absent on a legacy push -> strip shows as-is.
        state.deckPeeks[roomId] = {
          cards, deckSize: sizeKnown ? deckSize : null, receivedAt: Date.now(),
        };
      } else if (kind === 'deck' && sizeKnown) {
        // An EMPTY deck reveal with a real deckSize is the server's terminal-bury clear:
        // nothing the player saw remains on top, so the pinned strip must go.
        delete state.deckPeeks[roomId];
      } else if (kind === 'hole' && seat >= 0 && cards?.length) {
        const bySeat = { ...(state.peeks[roomId] || {}) };
        const previous = bySeat[seat] || [];
        bySeat[seat] = [...previous, ...cards.filter((card) => !previous.includes(card))];
        state.peeks[roomId] = bySeat;
      }
    },
    pupOwnCardsPeeked(state, action) {
      const { roomId, cards, byUsername } = action.payload;
      if (!cards?.length) return;
      const byCard = { ...(state.myPeeked[roomId] || {}) };
      cards.forEach((card) => {
        const previous = byCard[card] || [];
        const name = byUsername || 'another player';
        byCard[card] = previous.includes(name) ? previous : [...previous, name];
      });
      state.myPeeked[roomId] = byCard;
    },
    // A value-modifying Power-Up (Plus/Minus One, Suit Change, Set Ace, One Man's Trash, Magic
    // Trick) rewrote an existing playing card in place. Append a round-scoped "this card changed"
    // entry keyed by the card's IDENTITY (a HOLE mutation arrives only to the owner; a COMMUNITY
    // mutation is public). Entries STACK per card (Plus One then Minus One = two entries) so the
    // client can badge each change and reveal the true original on hold.
    //
    // Keyed by cardId, NOT by position: a discard or a Substitution renumbers every card after
    // it, and index-keyed provenance then either vanishes or — worse — reattaches to whichever
    // card slid into the slot.
    pupCardModified(state, action) {
      const { roomId, cardId, original, newCard, byUsername, code, causingCardId } = action.payload || {};
      if (roomId == null || cardId == null) return;
      const byRoom = { ...(state.cardMods[roomId] || {}) };
      const key = String(cardId);
      const list = byRoom[key] ? [...byRoom[key]] : [];
      list.push({
        by: byUsername || 'another player',
        original: original || null,
        current: newCard || null,
        code: code || null,
        causingCardId: causingCardId || null,
        at: Date.now(),
      });
      byRoom[key] = list;
      state.cardMods[roomId] = byRoom;
    },
    setMyPupCards(state, action) {
      state.inventory[action.payload.roomId] = action.payload.pupCards || [];
      if (action.payload.pupCardInstances !== undefined) {
        state.cardInstances[action.payload.roomId] = action.payload.pupCardInstances || [];
      }
      // The same push carries the AUTO-PLAY box, because a server-initiated auto-play has no
      // HTTP response to shrink it with.
      if (action.payload.queuedPupCards !== undefined) {
        state.queued[action.payload.roomId] = action.payload.queuedPupCards || [];
      }
    },
    setMyStagedPupCards(state, action) {
      state.staged[action.payload.roomId] = action.payload.stagedPupCards || [];
      state.stagedCommitted[action.payload.roomId] = action.payload.stagedCommitted || [];
    },
    pupNoticeAdded(state, action) {
      const { roomId, kind, text } = action.payload;
      if (!text) return;
      const notices = state.notices[roomId] || [];
      notices.push({ id: `${Date.now()}-${notices.length}`, kind: kind || 'info', text, receivedAt: Date.now() });
      state.notices[roomId] = notices.slice(-4);
    },
    pupNoticeExpired(state, action) {
      const { roomId, id } = action.payload;
      const next = (state.notices[roomId] || []).filter((notice) => notice.id !== id);
      if (next.length) state.notices[roomId] = next;
      else delete state.notices[roomId];
    },
    pupRoundDisplaysCleared(state, action) {
      const { roomId } = action.payload;
      // wildRefs is NOT swept here: it is authoritative state replaced by every game payload, and
      // the deal's payload already carries the new (empty) set. Sweeping it would blank the
      // badges until the next broadcast.
      ['publicReveals', 'peeks', 'myPeeked', 'cardMods', 'notices', 'deckPeeks', 'sniperGuessUsed']
        .forEach((key) => delete state[key][roomId]);
    },
    pupSeatRevealsCleared(state, action) {
      const { roomId, seat } = action.payload;
      if (state.publicReveals[roomId]) delete state.publicReveals[roomId][seat];
      if (state.peeks[roomId]) delete state.peeks[roomId][seat];
    },
  },
  extraReducers: (builder) => {
    [playPupCard, stagePupCard, unstagePupCard, queuePupCard, unqueuePupCard].forEach((thunk) => {
      builder.addCase(thunk.fulfilled, (state, action) => applyPrivateState(state, action.payload));
    });
    builder.addCase(sniperGuess.fulfilled, (state, action) => {
      state.sniperGuessUsed[action.payload.roomId] = true;
    });
    [playPupCard, stagePupCard, unstagePupCard, queuePupCard, unqueuePupCard, sniperGuess,
      pickPupTarget, pickPupDiscard, pickPupHandDiscard, pickPupCardTarget,
      multiDiscardPupCards, buryPupCard,
      performPupTrick, pickPupOptIn, pickPupValue, votePupPlay, pickContractSeat]
      .forEach((thunk) => builder.addCase(thunk.rejected, (state, action) => {
        state.error = action.payload;
      }));
  },
});

export const {
  privateStateReceived, snapshotReceived, roomCleared, allRoomsCleared,
  pupPlayPending, pupTargetChosen, pupPlayCleared, pupCardsRevealed, wildCardsReceived,
  pupPrivateReveal, pupOwnCardsPeeked, pupCardModified, setMyPupCards, setMyStagedPupCards,
  pupNoticeAdded, pupNoticeExpired, pupRoundDisplaysCleared, pupSeatRevealsCleared,
} = pupSlice.actions;

export const selectPupStack = (state, roomId) => state.pup.plays[roomId] || [];
export const selectPupInventory = (state, roomId) => state.pup.inventory[roomId] || [];

// Wildness is addressed POSITIONALLY. Components build a Set of these keys from the wild refs and
// ask `wildKeys.has(wildKey('COMMUNITY', -1, i))` — never "is this token in the wild list", which
// would light every card sharing the value.
export const wildKey = (zone, seat, index) => `${zone}:${seat}:${index}`;

// The id of the card at `index` of a positional id list, or null when this hand/board predates
// card ids. Provenance is looked up by that id, so it follows the card and not the slot.
export const cardIdAt = (ids, index) => (
  Array.isArray(ids) && index >= 0 && index < ids.length ? ids[index] : null
);

export default pupSlice.reducer;
